// React imports
import React, { useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/router'

// Util imports
import { initialMembersRolesTabList } from 'src/assets/SettingsConfig'

// Child imports
import TabSwitcher from 'src/components/common/containers/TabSwitcher'
import { UserPlus, Users } from 'react-feather'
import { Autocomplete, Button, Tooltip } from '@mui/material'

//  Type imports
import { SwitchableTabType } from 'src/lib/types/random'
import dynamic from 'next/dynamic'
import FloGenericDialog from 'src/components/common/feedback-and-dialog/GenericDialog'
import MultipleValuesInput from 'src/components/common/form-elements/MultipleValueInput'
import MultiSelectInput from 'src/components/common/form-elements/MultiSelectInput'
import NewEditRole from 'src/components/settings/members-roles/NewEditRole'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import useSWR, { mutate } from 'swr'
import { apiURI } from 'src/lib/utils/constants'
import { postRequest } from 'src/lib/core/api-client'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import Permission from 'src/services/PermissionService'
import { permissionType } from 'src/lib/utils/constants'
import { navigateToNewEditRole, validateEmail } from 'src/lib/utils/helpers'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useUser } from 'src/lib/contexts/UserContext'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { OrganizationConfigPayload, updateOrganizationConfig } from 'src/lib/core/api-methods'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { errorToast } from 'src/lib/utils/toasters'
import { Info } from '@phosphor-icons/react'
import InviteDropdown from './InviteDropDown'
import AddRecipient from './AddRecipient'

const MembersRoles: React.FC<{}> = ({}) => {
  const [tabList, setTabList] = useState<SwitchableTabType[] | undefined>(initialMembersRolesTabList)
  const [isInviteMemberDialogOpen, setIsInviteMemberDialogOpen] = useState<boolean>(false)
  const [isInviteSuccessful, setIsInviteSuccessful] = useState<boolean>(false)

  const router = useRouter()
  const { t } = useLocale()
  const {
    state: { superAdminEnabled },
    actions: { setSuperAdminEnabled }
  } = useOrganisationContext()
  const {
    getters: { merchant }
  } = useMerchant()
  const { setting_id, id } = router.query

  const { data: membersRolesResponse, isValidating: membersRolesLoading } = useSWR(
    [`/organization/roles-and-permission`, apiURI.AUTH_ENGINE],
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const handleInvite = () => {
    setIsInviteMemberDialogOpen(true)
  }

  const handleSuperAdminEnabledSwitch = async (value: boolean) => {
    const payload: OrganizationConfigPayload = {
      configuration: {
        super_admin_access_enabled: value
      }
    }
    try {
      if (!merchant?.accountId) return
      const response = (await updateOrganizationConfig(merchant?.accountId, payload)) as {
        data: {
          configuration: {
            super_admin_access_enabled: boolean
          }
        }
      }
      const isSuperAdminEnabled = response?.data?.configuration?.super_admin_access_enabled
      setSuperAdminEnabled(isSuperAdminEnabled)
      mutate([`/organization`, apiURI.AUTH_ENGINE])
    } catch (e) {
      console.error(e)
      errorToast(t('failed_to_save'))
    }
  }
  const hanldeAddRecipient = () => {
    router.push('/settings/members/recipient')
  }

  const getEditRoleId = useCallback(() => {
    if (Array.isArray(id)) return id[0]
    return id ?? ''
  }, [id])
  const { user } = useUser()

  if (Array.isArray(setting_id)) {
    if (setting_id[1] === 'new-role') return <NewEditRole />
    if (setting_id[1] === 'edit-role') return <NewEditRole roleId={getEditRoleId()} />
    if (setting_id[1] === 'recipient') return <AddRecipient />
  }

  return (
    <>
      <div className='absolute top-[40px] right-6 flex items-center gap-3'>
        {!Boolean(membersRolesLoading) && Boolean((membersRolesResponse as any)?.data) && (
          <>
            {user?.isAdmin && (
              <div className='flex flex-row items-center space-x-4 bg-gray-lightest px-3 py-1  rounded-xl border-2 border-gray-light'>
                <div className='flex space-x-1 items-center'>
                  <p className='text-sm font-medium text-coal-dark'>Shopflo Support Access</p>
                  <Tooltip title='This will enable Shopflo Support team to access your dashboard to help you with any issues you might be facing.'>
                    <Info size={16} className='text-coal-dark' />
                  </Tooltip>
                </div>
                <FloCheckbox
                  type='SWITCH'
                  label='Show only active members'
                  value={superAdminEnabled}
                  setValue={handleSuperAdminEnabledSwitch}
                />
              </div>
            )}
            <Permission
              permissionId={permissionType.ADD_CUSTOM_ROLE}
              quantity={(membersRolesResponse as any)?.data?.role_count}
            >
              <button
                disabled={user?.isDemoUser}
                id='new-role-button'
                className={`flex flex-row items-center gap-1 px-3 py-2 text-sm font-semibold rounded-lg outline outline-2 outline-gray-light ${
                  user?.isDemoUser
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed hover:bg-gray-200'
                    : 'bg-white text-coal-dark hover:bg-gray-lightest'
                }`}
                onClick={() => {
                  navigateToNewEditRole('new')
                }}
              >
                <Users id='new-role-button-icon' size={16} className='font-semibold text-coal-dark' />
                Create role
              </button>
            </Permission>
            <Permission
              permissionId={permissionType.ADD_MEMBER}
              quantity={(membersRolesResponse as any)?.data?.member_count}
            >
              <InviteDropdown onInvite={handleInvite} onAddRecipient={hanldeAddRecipient} />
            </Permission>
          </>
        )}
      </div>

      <TabSwitcher tabList={tabList} setTabList={setTabList} customClass='w-screen'>
        <div className='w-full'>
          {tabList?.map((tab: SwitchableTabType, index: number) => {
            const DynamicLookup = dynamic(() => import(`src/components/settings/members-roles/${tab?.childComponent}`))

            return <div key={`${tab?.name}_${index}`}>{tab?.isActive ? <DynamicLookup /> : <></>}</div>
          })}
        </div>
      </TabSwitcher>
      {isInviteMemberDialogOpen && (
        <InviteMemberDialog
          isInviteMemberDialogOpen={isInviteMemberDialogOpen}
          setIsInviteMemberDialogOpen={setIsInviteMemberDialogOpen}
          setIsInviteSuccessful={setIsInviteSuccessful}
        />
      )}
      {isInviteSuccessful && (
        <InviteSuccessfulDialog isInviteSuccessful={isInviteSuccessful} setIsInviteSuccessful={setIsInviteSuccessful} />
      )}
    </>
  )
}

interface InviteMemberDialogType {
  isInviteMemberDialogOpen: boolean
  setIsInviteMemberDialogOpen: (value: boolean) => void
  setIsInviteSuccessful: (value: boolean) => void
}

const InviteMemberDialog: React.FC<InviteMemberDialogType> = React.memo(
  ({ isInviteMemberDialogOpen, setIsInviteMemberDialogOpen, setIsInviteSuccessful }) => {
    const { data: rolesResponse, isValidating } = useSWR(
      [`/merchant-account/roles?limit=50&offset=1`, apiURI.AUTH_ENGINE],
      ([url, baseURI]) => fetcher(url, baseURI),
      staticOptions
    )

    const [invitedMembers, setInvitedMembers] = useState<string>('')
    const [selectedRoles, setSelectedRoles] = useState<string[]>([])
    const [openAlert, setOpenAlert] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const rolesOptions: any = []
    ;(rolesResponse as any)?.data?.forEach((role: any) => {
      const value = {
        value: role.id,
        label: role.name
      }
      rolesOptions.push(value)
    })

    const inviteMember = async () => {
      if (!Boolean(invitedMembers?.length) || !Boolean(selectedRoles?.length)) {
        setOpenAlert('incomplete_form')
        return
      }
      if (!validateEmail(invitedMembers)) {
        setOpenAlert('email_invalid')
        return
      }

      const membersArray = []
      membersArray.push(invitedMembers)
      try {
        const payload = {
          oids: membersArray,
          roles: selectedRoles
        }
        setIsLoading(true)
        const invitationPostResponse: any = await postRequest(
          '/merchant-account/invitations',
          apiURI.AUTH_ENGINE,
          payload
        )
        if (invitationPostResponse?.error) {
          setOpenAlert(invitationPostResponse?.error)
          setIsLoading(false)
          return
        }
        setOpenAlert('success')
        setIsInviteMemberDialogOpen(false)
        setIsInviteSuccessful(true)
        setIsLoading(false)
      } catch (e: any) {
        console.error(e)
        setOpenAlert(e.error ?? 'Error sending invite')
        setIsLoading(false)
      }
    }

    const handleCloseAlert = (event: any, reason: any) => {
      if (reason === 'clickaway') {
        return
      }
      setOpenAlert('')
    }

    const getAlertMsg = () => {
      if (openAlert === 'success') return 'Invitation sent successfully!'
      if (openAlert === 'incomplete_form') return 'Please fill all the fields.'
      if (openAlert === 'email_invalid') return 'Please enter a valid email adderss.'
      return openAlert ?? 'Something went wrong.'
    }

    if (!rolesResponse) return <></>

    return (
      <>
        <FloGenericDialog
          isOpen={isInviteMemberDialogOpen}
          width={600}
          handleClose={() => setIsInviteMemberDialogOpen(false)}
          handleJob={inviteMember}
          referrer='invite-member'
          jobButtonText={'Invite'}
          closeButtonText={'Cancel'}
          isLoading={isLoading}
          content={
            <>
              <div className='flex flex-col gap-4' data-track-id='invite-member-dialog-content'>
                <MultipleValuesInput label={'Invite by email'} setValues={setInvitedMembers} values={invitedMembers} />
                <MultiSelectInput
                  label={'Roles'}
                  setValues={setSelectedRoles}
                  values={selectedRoles}
                  options={rolesOptions}
                  placeHolderText='Select roles'
                />
              </div>
            </>
          }
          title={'Invite member'}
        />
        {openAlert.length ? (
          <Alerts
            open={true}
            duration={3000}
            closeHandler={handleCloseAlert}
            severity={openAlert === 'success' ? 'success' : 'error'}
            message={getAlertMsg()}
          />
        ) : null}
      </>
    )
  }
)

interface InviteSuccessfulDialogType {
  isInviteSuccessful: boolean
  setIsInviteSuccessful: (value: boolean) => void
}

const InviteSuccessfulDialog: React.FC<InviteSuccessfulDialogType> = React.memo(
  ({ isInviteSuccessful, setIsInviteSuccessful }) => {
    const { t } = useLocale()
    return (
      <FloGenericDialog
        isOpen={isInviteSuccessful}
        width={400}
        handleClose={() => setIsInviteSuccessful(false)}
        referrer='invite-member'
        handleJob={async () => setIsInviteSuccessful(false)}
        jobButtonText={'Okay'}
        content={
          <>
            <p className='text-sm font-normal'>{t('member_invite_successful_message') as string}</p>
          </>
        }
        title={'Invite sent!'}
      />
    )
  }
)

export default MembersRoles
